/**
 * What each kind of place looks like from above, and what the editor may ask about one.
 *
 * A floor plan earns its keep by being recognizable at a glance — you should find the table you
 * mean by looking, not by reading labels. That only works if a bar stool draws like a bar stool and
 * a lounger like a lounger, so the silhouettes are drawn from the real objects rather than from a
 * short list of geometric primitives.
 *
 * THE SILHOUETTES THEMSELVES ARE NOT HERE ANY MORE. They live in `contract/floor-art.json` and
 * arrive through `floor-art.generated.ts`, because the register draws the same rooms and the two
 * screens were inferring the drawing separately: this file keyed scenery off `kind`, the register
 * keyed everything off `shape`, and a palm — stored `kind:'tree' shape:'round'` — came out a rosette
 * here and a plain green disc on the owner's phone. Both were faithfully reading the row. All paths
 * are authored on a 100×100 viewBox and stretched to the place's own width and height, so proportion
 * comes from the furniture's dimensions and the outline stays one weight throughout
 * (`vectorEffect="non-scaling-stroke"`).
 *
 * NEITHER IS THE RULE ABOUT WHICH COLUMN GOVERNS. That is `artKeyFor` in the generated file, and
 * FloorMap calls it — the register calls its twin on the same row. Nothing here restates it: a
 * shared statement one of the two renderers does not actually call is the same defect with more
 * ceremony around it.
 *
 * What is left in this file is the EDITOR's half — what may be asked about a place, and what a new
 * one starts as — plus `runArt`, which is how this screen alone welds a wall to the next wall.
 */

import { DECOR_KINDS, RUN_DETAIL, RUN_KINDS, type SpotShape } from './floor-art.generated';

export { cssRadius, type Material, materialFor, type SpotShape } from './floor-art.generated';

/** Shapes that CSS can draw as a plain box — no SVG needed. */
export const CSS_SHAPES: SpotShape[] = ['rect', 'square', 'round'];

/**
 * Whether giving this silhouette a half-turn would visibly change it — whether Mirror is worth
 * offering on it at all.
 *
 * Most of the drawings map onto themselves turned around: the wall's coursing and the dock's
 * planks are ruled at even spacings, the steps' treads march at one rhythm, the palm's fronds
 * circle the trunk, the planter and the stool are rings, and every plain box, hexagon and circle
 * is symmetric about its own center. On any of those a Mirror button would visibly do nothing,
 * and a button that visibly does nothing teaches people to stop trusting the ones that do — the
 * same rule that keeps Turn off a square. Three silhouettes carry a feature that lives at one end
 * or along one side, and they are what the button is for: the sign's arrow, the counter's
 * bar-top-and-rail pair, and the lounger's backrest.
 */
export function mirrorable(kind: string, shape: string, assignable: boolean): boolean {
  if (!assignable) return kind === 'sign' || kind === 'counter';
  return shape === 'lounger';
}

/**
 * A sensible silhouette and footprint for each kind, used when something is first placed so the
 * designer starts from the right object instead of resizing a default square every time.
 */
export const KIND_DEFAULTS: Record<
  string,
  { shape: SpotShape; width: number; height: number; seats: number }
> = {
  table: { shape: 'square', width: 64, height: 64, seats: 4 },
  barSeat: { shape: 'stool', width: 34, height: 34, seats: 1 },
  beachChair: { shape: 'lounger', width: 34, height: 68, seats: 1 },
  other: { shape: 'rect', width: 64, height: 64, seats: 2 },
};

/**
 * What questions a given kind of place can actually answer.
 *
 * A bar stool and a beach lounger seat one person — that is what they ARE, not a number somebody
 * chose, and offering a Seats box on one invites an answer that cannot be true. "Can be joined to
 * another for a bigger group" is worse than merely redundant on them: joining is about pushing
 * TABLES together, so on a lounger it is a question about furniture that does not do that, and a
 * manager who ticks it has told the register something it will later act on.
 *
 * Declared here rather than inside the form because it governs the STORED ROW as much as the
 * screen. Switching a combinable four-top to a lounger used to leave `combinable: true` on the
 * record — invisible, because the form no longer showed the box that set it.
 */
export interface SeatingTraits {
  /** How many it seats, when that is fixed by what it is rather than chosen. */
  fixedSeats?: number;
  /** Whether pushing it together with a neighbor is a thing this kind of furniture does. */
  combinable: boolean;
  /** Whether "can it be moved" is a real question. A wall's answer is not interesting. */
  mobility: boolean;
}

const SEATING_TRAITS: Record<string, SeatingTraits> = {
  table: { combinable: true, mobility: true },
  other: { combinable: true, mobility: true },
  // One person, by definition, in both cases. The bar stool still slides along the rail and the
  // lounger still gets dragged down the beach, so mobility stays a real question for both.
  barSeat: { fixedSeats: 1, combinable: false, mobility: true },
  beachChair: { fixedSeats: 1, combinable: false, mobility: true },
};

/** Scenery answers none of it: nobody sits at a palm tree, so it is not asked about one. */
const SCENERY_TRAITS: SeatingTraits = { fixedSeats: 0, combinable: false, mobility: false };

export function seatingTraits(kind: string, assignable: boolean): SeatingTraits {
  if (!assignable) return SCENERY_TRAITS;
  return SEATING_TRAITS[kind] ?? { combinable: true, mobility: true };
}

/**
 * The row as it must be stored for this kind, whatever the previous kind left behind. Applied on
 * every change of kind so the record cannot keep an answer to a question this kind was never asked.
 */
export function traitsPatch(kind: string, assignable: boolean): Record<string, unknown> {
  const traits = seatingTraits(kind, assignable);
  return {
    ...(traits.fixedSeats === undefined ? {} : { seats: traits.fixedSeats }),
    ...(traits.combinable ? {} : { combinable: false }),
    ...(traits.mobility ? {} : { mobility: 'fixed' }),
  };
}

/**
 * Sensible starting size and silhouette for scenery, so a wall arrives as a wall rather than as a
 * 64x64 square the designer reshapes by hand every time.
 */
export const DECOR_DEFAULTS: Record<string, { width: number; height: number; shape: SpotShape }> = {
  wall: { width: 200, height: 16, shape: 'rect' },
  counter: { width: 220, height: 40, shape: 'rect' },
  dock: { width: 260, height: 80, shape: 'rect' },
  tree: { width: 56, height: 56, shape: 'round' },
  plant: { width: 40, height: 40, shape: 'round' },
  stairs: { width: 120, height: 48, shape: 'rect' },
  sign: { width: 80, height: 28, shape: 'rect' },
};

export function isRunKind(kind: string): boolean {
  return RUN_KINDS.includes(kind);
}

/**
 * The stretches of each edge that lie flush against another run of the same kind, measured in the
 * element's own pixels along that edge. The map works these out from the plan; the art only has to
 * know where not to draw.
 */
export interface WeldSpans {
  top: [number, number][];
  right: [number, number][];
  bottom: [number, number][];
  left: [number, number][];
}

/** A run's drawing, split so the fill can reach the very edge while the outline stops short. */
export interface RunArt {
  /** The whole footprint, filled to the edge, so two welded runs meet with no sliver of floor. */
  fill: string;
  /** The outline, drawn only along the stretches that face open floor. */
  outline: string;
  detail: string;
}

/** Rounded to a tenth for the same reason as the treads: 58.400000000000006 is not a coordinate. */
const tidy = (n: number) => Math.round(n * 10) / 10;

/** What is left of one edge once its welded stretches are taken out of it. */
function openRuns(length: number, welded: [number, number][]): [number, number][] {
  const shut = welded
    .map(([a, b]): [number, number] => [Math.max(0, Math.min(a, b)), Math.min(length, Math.max(a, b))])
    .filter(([a, b]) => b > a)
    .sort((a, b) => a[0] - b[0]);
  const open: [number, number][] = [];
  let at = 0;
  for (const [a, b] of shut) {
    if (a > at) open.push([at, a]);
    at = Math.max(at, b);
  }
  if (at < length) open.push([at, length]);
  return open;
}

/**
 * A run drawn in its own pixels, with its welded edges left open.
 *
 * The other silhouettes are authored on a 100-square and stretched, which is fine for a shape seen
 * on its own — but it puts the outline a PERCENTAGE of the size inside the box, so two flush runs
 * held their lines at different depths and could never read as one piece: the owner's L-shaped bar
 * top was two boxes with a double seam down the joint however exactly their endpoints met. Drawn
 * in pixels, the outline sits the same distance inside every run, the fill reaches the very edge,
 * and an edge that is welded to a neighbor simply is not drawn — the two runs become one shape
 * with one outline around it.
 */
export function runArt(kind: string, width: number, height: number, welds: WeldSpans): RunArt | undefined {
  if (!isRunKind(kind)) return undefined;
  const wide = width >= height;
  // The outline is inset one pixel so its two-pixel stroke ends exactly at the box edge, covering
  // the rim of the fill without spilling onto the neighboring floor.
  const edge = (spans: [number, number][], horizontal: boolean, at: number) =>
    spans.map(([a, b]) => (horizontal ? `M${tidy(a)} ${at} H${tidy(b)}` : `M${at} ${tidy(a)} V${tidy(b)}`));
  const outline = [
    ...edge(openRuns(width, welds.top), true, 1),
    ...edge(openRuns(height, welds.right), false, tidy(width - 1)),
    ...edge(openRuns(width, welds.bottom), true, tidy(height - 1)),
    ...edge(openRuns(height, welds.left), false, 1),
  ].join(' ');
  // Joints and planks cut across the run and stop short of the outline, as they always did. At a
  // welded butt joint no joint line is drawn at all, so two walls read as one long wall.
  const across = (fractions: number[]) =>
    fractions
      .map((f) =>
        wide ? `M${tidy(f * width)} 2 V${tidy(height - 2)}` : `M2 ${tidy(f * height)} H${tidy(width - 2)}`,
      )
      .join(' ');
  // Rails and beams run WITH it — and keep going straight through a welded end, because a bar top
  // extended by a second run is one rail, not two rails with a break where the pieces meet.
  const covered = (spans: [number, number][], at: number) => spans.some(([a, b]) => at >= a && at <= b);
  const along = (fractions: number[]) =>
    fractions
      .map((f) => {
        if (wide) {
          const y = tidy(f * height);
          const x0 = covered(welds.left, y) ? 0 : 2;
          const x1 = covered(welds.right, y) ? width : width - 2;
          return `M${x0} ${y} H${tidy(x1)}`;
        }
        const x = tidy(f * width);
        const y0 = covered(welds.top, x) ? 0 : 2;
        const y1 = covered(welds.bottom, x) ? height : height - 2;
        return `M${x} ${y0} V${tidy(y1)}`;
      })
      .join(' ');
  // THE RHYTHM IS THE SHARED HALF. Block coursing on a wall, the bar top and its guest rail on a
  // counter, planks and beams on the dock: those fractions say what the thing IS, and the register
  // rules its own runs at the same ones. What is local to this screen is only the welding — where a
  // line stops because a neighbor is standing there. See contract/floor-art.json.
  const rhythm = RUN_DETAIL[kind] ?? {};
  const detail = [across(rhythm.across ?? []), along(rhythm.along ?? [])].filter(Boolean).join(' ');
  return { fill: `M0 0 H${width} V${height} H0 Z`, outline, detail };
}

/**
 * Everything that can be put on a plan, and what it starts as.
 *
 * This exists because the seeded plan had a sea wall and palm trees on it and the screen offered no
 * way to add either: double-clicking the floor always produced a table, and the only route to a palm
 * was to add a table and then change what it was. The room's own furniture looked like something
 * only the database could place.
 *
 * One list, read by the palette and by the editor, so a kind cannot be offered in one and unknown to
 * the other.
 */
export interface PaletteEntry {
  kind: string;
  label: string;
  /** Scenery drops straight onto the plan; somewhere to sit opens for a name first. */
  assignable: boolean;
}

export const PALETTE: { group: string; items: PaletteEntry[] }[] = [
  {
    group: 'Somewhere to sit',
    items: [
      { kind: 'table', label: 'Table', assignable: true },
      { kind: 'barSeat', label: 'Bar stool', assignable: true },
      { kind: 'beachChair', label: 'Beach chair', assignable: true },
    ],
  },
  {
    group: 'Part of the room',
    items: [
      { kind: 'wall', label: 'Wall', assignable: false },
      { kind: 'counter', label: 'Bar counter', assignable: false },
      { kind: 'dock', label: 'Dock', assignable: false },
      { kind: 'tree', label: 'Palm or tree', assignable: false },
      { kind: 'plant', label: 'Planter', assignable: false },
      { kind: 'stairs', label: 'Steps', assignable: false },
      { kind: 'sign', label: 'Sign or marker', assignable: false },
    ],
  },
];

/**
 * What to call something that was never given a name.
 *
 * Scenery goes straight down without a dialog, because nobody types "Palm 3" — so a wall dropped
 * from the palette has no name at all, and every sentence built from one read " — no tab can be
 * opened here". Hovering a dock and being told nothing about it is the same complaint as not being
 * able to tell a dock from a wall. What it IS is the name it has.
 */
export function kindLabel(kind: string): string {
  for (const group of PALETTE) {
    const hit = group.items.find((i) => i.kind === kind);
    if (hit) return hit.label.toLowerCase();
  }
  return 'element';
}

/**
 * Whether this kind is somewhere a guest sits, and therefore something a tab can be opened against.
 *
 * Read off the shared list rather than off `DECOR_DEFAULTS`, which used to answer this by having an
 * entry: that made "is a dock scenery" a fact about a table of starting SIZES, and the register had
 * to know the same list to draw one. It is one list now, in `contract/floor-art.json`.
 */
export function isAssignableKind(kind: string): boolean {
  return !DECOR_KINDS.includes(kind);
}

/** The row a newly placed element starts as, before anybody edits it. */
export function newSpotOfKind(kind: string): Record<string, unknown> {
  const decor = DECOR_DEFAULTS[kind];
  const base = decor
    ? { assignable: false, ...decor }
    : {
        assignable: true,
        shape: 'square' as SpotShape,
        width: 64,
        height: 64,
        seats: 4,
        ...(KIND_DEFAULTS[kind] ?? {}),
      };
  return {
    label: '',
    kind,
    mobility: 'fixed',
    combinable: false,
    active: true,
    ...base,
    ...traitsPatch(kind, !decor),
  };
}

/** The shapes worth offering for a given kind, most likely first. */
export function shapesFor(kind: string): { value: SpotShape; label: string }[] {
  if (kind === 'barSeat') {
    return [
      { value: 'stool', label: 'Bar stool' },
      { value: 'round', label: 'Round' },
      { value: 'square', label: 'Square' },
    ];
  }
  if (kind === 'beachChair') {
    return [
      { value: 'lounger', label: 'Lounger' },
      { value: 'rect', label: 'Rectangle' },
      { value: 'round', label: 'Round' },
    ];
  }
  return [
    { value: 'square', label: 'Square table' },
    { value: 'rect', label: 'Rectangular table' },
    { value: 'round', label: 'Round table' },
    { value: 'hex', label: 'Hexagon table' },
  ];
}
